import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPut } from '../client';
import { qk } from './keys';
import type {
  AutoBookingSettings,
  AutoBookingSettingsInput,
  AutoBookingStatus,
  BookingPriorityCountry,
  CurrentLocation,
  Locale,
  UserProfile,
  UserProfilePatch,
} from '../types';

export function useMe(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: qk.me,
    queryFn: () => apiGet<UserProfile>('/me'),
    enabled: opts?.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UserProfilePatch) => apiPatch<UserProfile>('/me', patch),
    onSuccess: (u) => {
      qc.setQueryData(qk.me, u);
    },
  });
}

export function useCurrentLocation() {
  return useQuery({
    queryKey: qk.location,
    queryFn: async () => {
      try {
        return await apiGet<CurrentLocation>('/me/current-location');
      } catch {
        return null;
      }
    },
  });
}

export interface LocationInput {
  country_code: string;
  city_id?: string | null;
  lat?: number | null;
  lng?: number | null;
}

export function useUpdateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LocationInput) => apiPut<CurrentLocation>('/me/current-location', body),
    onSuccess: (data) => {
      qc.setQueryData(qk.location, data);
      // After setting a location the backend stops 404-ing /current,
      // /breakdown and /history — re-query all three.
      qc.invalidateQueries({ queryKey: qk.safetyCurrent });
      qc.invalidateQueries({ queryKey: qk.safetyBreakdown });
      qc.invalidateQueries({ queryKey: ['safety-score', 'me', 'history'] });
    },
  });
}

export function useLocale() {
  return useQuery({
    queryKey: qk.locale,
    queryFn: () => apiGet<{ locale: Locale }>('/me/locale'),
  });
}

export function useUpdateServerLocale() {
  return useMutation({
    mutationFn: (locale: Locale) => apiPut<void>('/me/locale', { locale }),
  });
}

export function useAutoBookingSettings() {
  return useQuery({
    queryKey: qk.autoBookingSettings,
    queryFn: () => apiGet<AutoBookingSettings>('/me/auto-booking-settings'),
  });
}

export function useUpdateAutoBookingSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: AutoBookingSettingsInput) =>
      apiPut<AutoBookingSettings>('/me/auto-booking-settings', patch),
    onSuccess: (data) => {
      qc.setQueryData(qk.autoBookingSettings, data);
    },
  });
}

export function useAutoBookingStatus() {
  return useQuery({
    queryKey: qk.autoBookingStatus,
    // Endpoint added in Addendum-01 Q3 — surfaces top-up gap.
    queryFn: async () => {
      try {
        return await apiGet<AutoBookingStatus>('/me/wallet/auto-booking-status');
      } catch {
        return null;
      }
    },
    staleTime: 30_000,
  });
}

export function useBookingPriorities() {
  return useQuery({
    queryKey: qk.bookingPriorities,
    queryFn: () => apiGet<BookingPriorityCountry[]>('/me/booking-priorities'),
  });
}

export function useUpdateBookingPriorities() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: Array<{ country_code: string; position: number }>) =>
      apiPut<void>('/me/booking-priorities', { items }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.bookingPriorities });
    },
  });
}
